// The view state: everything a shared link or a lesson step needs to put the app
// back into the same view. The contract is docs/contracts/view-state.md and the
// JSON form is schemas/view-state.schema.json; url.ts writes it into the address.
//
// The state is plain JSON (no classes, no undefined), so it compares, copies and
// validates as data. Each section names the ticket that reads it; the shell stores
// and round-trips every section, also those whose features do not exist yet.

export type Vec3 = [number, number, number];

/** A cutaway plane in the scene frame: points with dot(normal, p) > offset are cut away. */
export interface CutPlane {
  normal: Vec3;
  /** Metres along the normal from the scene origin. */
  offset: number;
}

export interface Camera {
  /** Metres, in the scene frame (ADR 0008 fixes the axes). */
  position: Vec3;
  /** The point the camera looks at, metres. */
  target: Vec3;
  /** Vertical field of view in degrees, (0, 180). */
  fov: number;
}

export interface ViewState {
  /** Engine id, the name of its folder in data/engines/ (P7.4 switches engines). */
  engine: string;
  /** The named view (data/views/*.toml) this state is based on, or null. */
  view: string | null;
  controls: {
    /** Throttle opening, 0 closed to 1 wide open. */
    throttle: number;
    /** Ignition switched on. */
    ignition: boolean;
  };
  audio: {
    /** Output gain, 0 to 1. */
    volume: number;
    muted: boolean;
    /** Listener position id (P2), or null for the engine's default. */
    listener: string | null;
  };
  /** "auto" lets the load monitor choose (P1.13); otherwise a tier id from data/quality/. */
  quality: string;
  /** null leaves the camera where the scene puts it (P1.12, P1.14). */
  camera: Camera | null;
  /** Cutaway planes, at most MAX_CUT_PLANES (P1.14's cutaway panel). */
  cutaway: CutPlane[];
  time: {
    /** Simulated seconds per real second, 0 to 1 (the host control time_scale). */
    scale: number;
    paused: boolean;
    /** Crank angle in degrees, [0, 720), held while paused; null for wherever it stopped (P6.3). */
    angle: number | null;
    /** How the scene shows the crank's motion: slowed to a strobe view (the default) or at true speed (P1.12). */
    display: DisplayMode;
  };
  panels: {
    /** Feature ids of the side panels that are collapsed. */
    collapsed: string[];
  };
  /** The lesson and its step, counted from 1 (P6.2), or null. */
  lesson: { id: string; step: number } | null;
}

export const MAX_CUT_PLANES = 6;

/** "strobe" shows at most one apparent revolution per second; "true-speed" shows the crank as it turns. */
export type DisplayMode = "strobe" | "true-speed";
export const DISPLAY_MODES: readonly DisplayMode[] = ["strobe", "true-speed"];

/** The state of a fresh app with no address parameters. */
export const DEFAULT_VIEW_STATE: ViewState = {
  engine: "ct",
  view: null,
  controls: { throttle: 0, ignition: false },
  audio: { volume: 0.8, muted: false, listener: null },
  quality: "auto",
  camera: null,
  cutaway: [],
  time: { scale: 1, paused: false, angle: null, display: "strobe" },
  panels: { collapsed: [] },
  lesson: null,
};

/** A view state with any part left out, as named views and store patches give it. */
export type PartialViewState = DeepPartial<ViewState>;

type DeepPartial<T> = T extends readonly unknown[]
  ? T
  : T extends object
    ? { [K in keyof T]?: DeepPartial<T[K]> | null }
    : T;

/** Lower-case ids: letters, digits, '.', '_' and '-'. They need no escaping in an address. */
export const ID_PATTERN = /^[a-z0-9][a-z0-9._-]*$/;

export function cloneState(state: ViewState): ViewState {
  return structuredClone(state);
}

/** Deep equality of two JSON values. */
export function jsonEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => jsonEqual(v, b[i]));
  }
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  return ka.every((k) => jsonEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
}
